import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, money2, dateShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionTitle, EmptyState, Field, Pill, MetricCard } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-lime";

const TONE = {
  Draft: "bg-line text-stone",
  Sent: "bg-[#EDEFC4] text-ink",
  "Partially Paid": "bg-[#DDE3C8] text-ink",
  Overdue: "bg-[#F6DED6] text-[#A8492E]",
  Paid: "bg-[#BEC221] text-ink",
  Refunded: "bg-[#E07A5F] text-white",
  Void: "bg-ink text-white",
};

export const InvoiceStatus = ({ status, testId }) =>
  status ? <Pill testId={testId} tone={TONE[status] || "bg-line text-stone"}>{status}</Pill> : null;

export function InvoiceForm({ projectId, onCreated, open: openProp, onClose }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp === undefined ? openState : openProp;
  const setOpen = (v) => (openProp === undefined ? setOpenState(v) : (!v && onClose?.()));
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    project_id: projectId || "", title: "", invoice_type: "Deposit", tax: 0,
    due_date: "", notes: "", internal_notes: "", allow_partial: true, minimum_payment: 0,
  });
  const [lines, setLines] = useState([{ description: "", amount: "" }]);

  useEffect(() => {
    if (open && !projectId) api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, [open, projectId]);

  const total = lines.reduce((a, l) => a + (Number(l.amount) || 0), 0) + (Number(form.tax) || 0);

  const submit = async (e) => {
    e.preventDefault();
    const line_items = lines
      .filter((l) => l.description.trim() && Number(l.amount) > 0)
      .map((l) => ({ description: l.description.trim(), amount: Number(l.amount) }));
    if (!line_items.length) return toast.error("Add at least one line item with an amount");
    try {
      const { data } = await api.post("/invoices", {
        ...form,
        project_id: projectId || form.project_id,
        tax: Number(form.tax) || 0,
        minimum_payment: Number(form.minimum_payment) || 0,
        due_date: form.due_date || null,
        line_items,
      });
      toast.success(`Invoice ${data.number} created as a draft`);
      setOpen(false);
      setLines([{ description: "", amount: "" }]);
      setForm({ ...form, title: "", notes: "", internal_notes: "", tax: 0, due_date: "" });
      onCreated?.(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!open)
    return (
      <button data-testid="new-invoice-btn" onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark ${openProp === undefined ? "" : "hidden"}`}>
        <Plus className="h-4 w-4" strokeWidth={1.5} /> New Invoice
      </button>
    );

  return (
    <form onSubmit={submit} data-testid="invoice-form" className="card-surface w-full space-y-6 p-6">
      <div className="grid gap-5 sm:grid-cols-2">
        {!projectId && (
          <Field label="Project">
            <select data-testid="invoice-project-select" required value={form.project_id}
              onChange={(e) => setForm({ ...form, project_id: e.target.value })} className={input}>
              <option value="">Select a project…</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Invoice Title">
          <input data-testid="invoice-title-input" required value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="50% Project Deposit" className={input} />
        </Field>
        <Field label="Type">
          <select data-testid="invoice-type-select" value={form.invoice_type}
            onChange={(e) => setForm({ ...form, invoice_type: e.target.value })} className={input}>
            {["Deposit", "Progress Payment", "Final Balance", "Design Fee", "Other"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Due Date">
          <input data-testid="invoice-due-input" type="date" value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={`num ${input}`} />
        </Field>
      </div>

      <div className="space-y-3">
        <div className="overline">Line Items</div>
        {lines.map((l, i) => (
          <div key={i} className="flex gap-3">
            <input data-testid={`invoice-line-desc-${i}`} value={l.description}
              onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
              placeholder="Description" className={input} />
            <input data-testid={`invoice-line-amount-${i}`} type="number" step="0.01" value={l.amount}
              onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
              placeholder="0.00" className={`num w-40 ${input}`} />
            {lines.length > 1 && (
              <button type="button" data-testid={`invoice-line-remove-${i}`} onClick={() => setLines(lines.filter((_, j) => j !== i))}
                className="rounded-sm border border-line px-3 text-stone hover:text-clay" aria-label="Remove line">
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
        ))}
        <button type="button" data-testid="invoice-add-line-btn" onClick={() => setLines([...lines, { description: "", amount: "" }])}
          className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">+ Add line</button>
      </div>

      <div className="grid gap-5 sm:grid-cols-3">
        <Field label="Tax">
          <input data-testid="invoice-tax-input" type="number" step="0.01" value={form.tax}
            onChange={(e) => setForm({ ...form, tax: e.target.value })} className={`num ${input}`} />
        </Field>
        <Field label="Minimum Payment" hint="Leave 0 for no minimum">
          <input data-testid="invoice-minimum-input" type="number" step="0.01" value={form.minimum_payment}
            onChange={(e) => setForm({ ...form, minimum_payment: e.target.value })} className={`num ${input}`} />
        </Field>
        <Field label="Partial Payments">
          <label className="flex items-center gap-2 pt-2 text-sm text-stone">
            <input data-testid="invoice-allow-partial-toggle" type="checkbox" checked={form.allow_partial}
              onChange={(e) => setForm({ ...form, allow_partial: e.target.checked })} className="accent-lime" />
            Client may pay in instalments
          </label>
        </Field>
        <Field label="Note to Client" className="sm:col-span-2">
          <textarea data-testid="invoice-notes-input" rows={2} value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} className={input} />
        </Field>
        <Field label="Internal Note">
          <textarea data-testid="invoice-internal-notes-input" rows={2} value={form.internal_notes}
            onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} className={input} />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-line pt-5">
        <span className="num mr-auto text-lg text-ink" data-testid="invoice-form-total">Total {money2(total)}</span>
        <button data-testid="invoice-submit-btn" type="submit"
          className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Save Draft</button>
        <button type="button" onClick={() => setOpen(false)}
          className="rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:bg-line/50">Cancel</button>
      </div>
    </form>
  );
}

export function InvoiceTable({ rows, showProject = true }) {
  const navigate = useNavigate();
  if (!rows.length)
    return <EmptyState testId="invoices-empty" title="No invoices yet"
      body="Invoices you create will appear here with their payment status." />;
  return (
    <div className="card-surface overflow-x-auto scroll-thin">
      <table className="w-full min-w-[760px] text-sm" data-testid="invoices-table">
        <thead>
          <tr className="border-b border-line text-left">
            {["Invoice", showProject ? "Project" : "", "Due", "Total", "Paid", "Balance", "Status"].filter(Boolean).map((h) => (
              <th key={h} className="overline px-5 py-3">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`invoice-row-${r.id}`} onClick={() => navigate(`/invoices/${r.id}`)}
              className="cursor-pointer border-b border-line last:border-0 hover:bg-bone">
              <td className="px-5 py-3">
                <Link to={`/invoices/${r.id}`} onClick={(e) => e.stopPropagation()} className="text-ink hover:text-lime-dark">
                  <span className="num">{r.number}</span> · {r.title}
                </Link>
              </td>
              {showProject && <td className="px-5 py-3 text-stone">{r.project_name || "—"}</td>}
              <td className="num px-5 py-3 text-stone">{r.due_date ? dateShort(r.due_date) : "—"}</td>
              <td className="num px-5 py-3 text-right">{money(r.total)}</td>
              <td className="num px-5 py-3 text-right text-lime-dark">{money(r.amount_paid)}</td>
              <td className="num px-5 py-3 text-right">{r.status === "Void" ? "—" : money(r.balance)}</td>
              <td className="px-5 py-3"><InvoiceStatus status={r.status} testId={`invoice-status-${r.id}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Invoices() {
  const { isStaff } = useAuth();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const archived = params.get("archived") === "1";
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get("/invoices", { params: { ...(status ? { status } : {}), ...(archived ? { archived: true } : {}) } })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
    if (isStaff) api.get("/invoices/summary").then((r) => setSummary(r.data)).catch(() => {});
  };

  useEffect(load, [status, archived, isStaff]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = rows.filter((r) => ["Sent", "Partially Paid", "Overdue"].includes(r.status));
  const outstanding = open.reduce((a, r) => a + (r.balance || 0), 0);

  return (
    <div className="space-y-12" data-testid="invoices-page">
      <PageHeader
        overline={isStaff ? "Billing" : "Your Invoices"}
        title={isStaff ? "Invoices & Payments" : "Invoices & Payments"}
        subtitle={isStaff
          ? "Every invoice, deposit and Square transaction across your projects."
          : "Review what’s due, pay securely, and keep track of everything you’ve already paid."}
        actions={isStaff && (
          <button data-testid="new-invoice-btn" onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> New Invoice
          </button>
        )}
      />

      {isStaff && creating && (
        <InvoiceForm open onClose={() => setCreating(false)} onCreated={(d) => { setCreating(false); load(d); }} />
      )}

      {isStaff && summary && (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          <MetricCard testId="billing-metric-invoiced" label="Total Invoiced" value={money(summary.invoiced)} />
          <MetricCard testId="billing-metric-received" label="Payments Received" value={money(summary.received)} accent />
          <MetricCard testId="billing-metric-outstanding" label="Outstanding" value={money(summary.outstanding)} hint={`${summary.open_count} open`} to="/invoices" />
          <MetricCard testId="billing-metric-overdue" label="Overdue" value={summary.overdue} to="/invoices?status=Overdue" />
          <MetricCard testId="billing-metric-refunded" label="Refunded" value={money(summary.refunded)} hint={`${summary.failed_payments} failed payments`} />
        </div>
      )}

      {!isStaff && (
        <div className="grid gap-5 sm:grid-cols-3" data-testid="client-invoice-summary">
          <MetricCard testId="client-metric-outstanding" label="Outstanding Balance" value={money(outstanding)} accent />
          <MetricCard testId="client-metric-open-invoices" label="Invoices to Pay" value={open.length} />
          <MetricCard testId="client-metric-paid-invoices" label="Paid in Full" value={rows.filter((r) => r.status === "Paid").length} />
        </div>
      )}

      {isStaff && (
        <div className="flex flex-wrap gap-2" data-testid="invoice-status-filters">
          {["", "Draft", "Sent", "Partially Paid", "Overdue", "Paid", "Void"].map((s) => (
            <button key={s || "all"} data-testid={`invoice-filter-${(s || "all").toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setParams(s ? { status: s } : {})}
              className={`rounded-sm border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${
                !archived && status === s ? "border-lime bg-[#EDEFC4] text-ink" : "border-line bg-white text-stone hover:text-ink"}`}>
              {s || "All"}
            </button>
          ))}
          <button data-testid="invoice-filter-archived" onClick={() => setParams(archived ? {} : { archived: "1" })}
            className={`rounded-sm border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${
              archived ? "border-lime bg-[#EDEFC4] text-ink" : "border-line bg-white text-stone hover:text-ink"}`}>
            Archived
          </button>
        </div>
      )}

      <section>
        <SectionTitle>{isStaff ? "All Invoices" : "Your Invoices"}</SectionTitle>
        <InvoiceTable rows={rows} />
      </section>
    </div>
  );
}
